import { HistoryGuard } from './history-guard';
import { HISTORY_LIMIT, type ChangeEntry, type UndoHistoryItem, type UndoListener, type UndoState, type UndoStep } from './types';
import type { CommandLabel } from '../mutations/labels';

export const MAX_UNDO_STEPS = 100;

/** In-memory undo and redo stacks of one store instance (not persisted). */
export class UndoManager {
  readonly guard = new HistoryGuard();
  private undoStack: readonly UndoStep[] = [];
  private redoStack: readonly UndoStep[] = [];
  private readonly listeners = new Set<UndoListener>();

  constructor(private readonly capacity: number = MAX_UNDO_STEPS) {}

  getState(): UndoState {
    const undoTop = this.undoStack[this.undoStack.length - 1];
    const redoTop = this.redoStack[this.redoStack.length - 1];
    return {
      canUndo: undoTop !== undefined,
      canRedo: redoTop !== undefined,
      undoLabel: undoTop?.label ?? null,
      redoLabel: redoTop?.label ?? null,
      history: this.history(),
    };
  }

  subscribe(listener: UndoListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  peekUndo(): UndoStep | undefined {
    return this.undoStack[this.undoStack.length - 1];
  }

  /**
   * The commands in the order they were issued, newest first: the redo stack holds the commands
   * undone after the ones on the undo stack, its top being the oldest of them.
   */
  private history(): UndoHistoryItem[] {
    const undone = this.redoStack.map((step): UndoHistoryItem => ({ ...step, undone: true }));
    const done = [...this.undoStack].reverse().map((step): UndoHistoryItem => ({ ...step, undone: false }));
    return [...undone, ...done].slice(0, HISTORY_LIMIT);
  }

  peekRedo(): UndoStep | undefined {
    return this.redoStack[this.redoStack.length - 1];
  }

  /** A new command: it becomes the latest undo step and invalidates the redo history. */
  record(label: CommandLabel, changes: readonly ChangeEntry[], atMs: number): void {
    const step: UndoStep = { label, entries: this.guard.stamp(changes), atMs };
    this.undoStack = [...this.undoStack, step].slice(-this.capacity);
    this.redoStack = [];
    this.notify();
  }

  /** The top undo step was undone; it can now be redone. */
  completeUndo(written: ReadonlyMap<string, string>): void {
    const step = this.undoStack[this.undoStack.length - 1];
    if (step === undefined) return;
    this.guard.applied(written);
    this.undoStack = this.undoStack.slice(0, -1);
    this.redoStack = [...this.redoStack, step];
    this.notify();
  }

  /** The top redo step was redone; it can now be undone again. */
  completeRedo(written: ReadonlyMap<string, string>): void {
    const step = this.redoStack[this.redoStack.length - 1];
    if (step === undefined) return;
    this.guard.applied(written);
    this.redoStack = this.redoStack.slice(0, -1);
    this.undoStack = [...this.undoStack, step].slice(-this.capacity);
    this.notify();
  }

  /** The top undo step cannot be applied: forget it and the redo history. */
  dropUndoStep(): void {
    this.undoStack = this.undoStack.slice(0, -1);
    this.redoStack = [];
    this.notify();
  }

  /** The top redo step cannot be applied: forget it and the redo steps that depend on it. */
  dropRedoSteps(): void {
    this.redoStack = [];
    this.notify();
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.guard.clear();
    this.notify();
  }

  private notify(): void {
    const state = this.getState();
    for (const listener of this.listeners) listener(state);
  }
}
